// AI-CONTEXT: See docs/features/compare-two-periods.md
//
// Aligns two single-period category series into grouped-bar rows. Each input is
// an array of { name, value } (one bar per category, e.g. answer options or
// matrix rows for ONE period). The output is one row per category carrying both
// periods' values — { name, a, b } — ready for a recharts grouped BarChart.
//
// Categories are the union of both series, in A's order first, then any
// B-only categories appended (stable, so the axis order is predictable). A
// category missing from one side gets null there, so the bar simply doesn't
// render for that period (rather than a misleading zero).

/**
 * @typedef {{name: string, value: number|null}} CategoryPoint
 * @typedef {{name: string, a: number|null, b: number|null}} GroupedBarRow
 */

/**
 * @param {CategoryPoint[]|null|undefined} seriesA - Primary period points
 * @param {CategoryPoint[]|null|undefined} seriesB - Compare period points
 * @returns {GroupedBarRow[]}
 */
const mergeCategorySeries = (seriesA, seriesB) => {
    const a = Array.isArray(seriesA) ? seriesA : []
    const b = Array.isArray(seriesB) ? seriesB : []

    // First occurrence of a name wins its value (and its axis position).
    const firstWins = (points) => {
        const map = new Map()
        for (const p of points) if (!map.has(p.name)) map.set(p.name, p.value)
        return map
    }
    const byNameA = firstWins(a)
    const byNameB = firstWins(b)

    const order = []
    const seen = new Set()
    for (const p of [...a, ...b]) {
        if (!seen.has(p.name)) {
            seen.add(p.name)
            order.push(p.name)
        }
    }

    return order.map(name => ({
        name,
        a: byNameA.has(name) ? byNameA.get(name) : null,
        b: byNameB.has(name) ? byNameB.get(name) : null,
    }))
}

export default mergeCategorySeries
